import {
  Button,
  ButtonSize,
  ButtonTheme,
  TextInput,
  TextInputTheme,
  Switch,
  Datepicker,
  Checkbox,
  CheckboxTheme,
} from '@dbrainio/shared-ui'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import Select, { MultiValue } from 'react-select'
import { Client, docTypes } from '../../@types/common'
import { AddClientPayload } from '../../api/api'
import { Utils } from '../../utils/utils'
import CloseIcon from '../CloseIcon/CloseIcon'
import style from './AddClientForm.module.css'
import { defaultLabels, allLabels, allHitlLabels } from './consts'
import CreatableSelect from 'react-select/creatable';


interface Props {
  owner?: string
  editedClient?: Client | null
  isLoading?: boolean
  isDeleteLoading?: boolean
  onDelete: () => void
  onPause: () => void
  onActivate: () => void
  onSubmit: (payload: AddClientPayload) => void
  onCopy: (value: string) => void
  onClose: () => void
}

enum limitType {
  day = 'day',
  month = 'month',
  year = 'year',
  alltime = 'alltime',
}

type ValidLimitType = keyof typeof limitType;

const initialLimits = {
  day: null,
  month: null,
  year: null,
  alltime: null,
}

const initialLabels = {
  day: 'Daily',
  month: 'Monthly',
  year: 'Annualy',
  alltime: 'All time',
}

const limitsOrder: ValidLimitType[] = [limitType.day, limitType.month, limitType.year, limitType.alltime]

export default function AddClientForm({
  owner,
  editedClient,
  isDeleteLoading,
  isLoading,
  onDelete,
  onClose,
  onCopy,
  onPause,
  onSubmit,
  onActivate,
}: Props) {
  useEffect(() => {
    if (editedClient) {
      const {
        expire,
        labels,
        license,
        client,
        pipeline_link,
        website,
        limits,
      } = editedClient
      setIsHitl(!!labels?.length && labels.some((l) => l.startsWith('hitl_')))
      setToken(license)
      setCompany(client)
      const labelsPayload = labels?.filter((l) => !l.startsWith('hitl_')).filter((value, index, self) => {
        return self.indexOf(value) === index;
      }) || [];
      setLabels(
        labelsPayload?.length
          ? labelsPayload
          : defaultLabels,
      )
      setHitlLabels(
        labels?.length
          ? labels.filter((l) => l.startsWith('hitl_'))
          : hitlLabels,
      )


      if (limits?.success) {
        const result: Partial<Record<ValidLimitType, null | number>> = {}
        limitsOrder.forEach((key: ValidLimitType) => {
          const target = limits?.success || {}
          const value = target.hasOwnProperty(key) ? target?.[key] : null
          result[key] = value
        })
        console.log(limits?.success);

        setLimits((prev) => ({ ...prev, ...result }))
      }

      pipeline_link && setPipeline(pipeline_link)
      website && setWebsite(website)
      expire && setExpire(new Date(expire * 1000))
    }

    return () => { }
  }, [editedClient])

  const [isHitl, setIsHitl] = useState(
    editedClient?.labels?.includes('hitl') || false,
  )

  const [generatedToken, setToken] = useState(
    editedClient?.license || Utils.generateRandomString(24),
  )
  const [company, setCompany] = useState(editedClient?.client || '')
  const [expire, setExpire] = useState<Date | null>(null)
  const [limits, setLimits] = useState<Record<string, (number | null)>>(initialLimits)
  const [pipeline, setPipeline] = useState(editedClient?.pipeline_link || '')
  const [website, setWebsite] = useState(editedClient?.website || '')
  const [labels, setLabels] = useState(defaultLabels)
  const [hitlLabels, setHitlLabels] = useState<string[]>([])

  const handleDateChange = (date: Date) => {
    setExpire(date)
  }

  const handleHitlChange = (isHitl: boolean) => {
    setIsHitl(isHitl)
    if (isHitl) {
      setLabels([...labels, 'hitl'])
    } else {
      setLabels(labels.filter((l) => l !== 'hitl'))
    }
  }

  const handleCopy = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    e.stopPropagation()

    onCopy(generatedToken)
  }

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    onClose()
  }

  const handlePause = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    onPause()
  }

  const handleActivate = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e.preventDefault()
    onActivate()
  }

  const handleDelete = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault()
    onDelete()
  }

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const limitsPayload: {
      success: {
        alltime?: number | null
        year?: number | null
        month?: number | null
        day?: number | null
      }
    } = {
      success: {
        ...limits
      },
    }
    
    onSubmit({
      client: company,
      license: generatedToken,
      expire: moment(expire).unix(),
      website,
      pipeline_link: pipeline,
      labels: isHitl ? [...labels, ...hitlLabels] : labels,
      limits: limitsPayload,
    })
  }

  const onLabelsChange = (
    values: MultiValue<{
      value: string
      label: string
    }>,
  ) => {
    setLabels(values.map((v) => v.value))
  }

  const onHitlLabelChange = (
    values: MultiValue<{
      value: string
      label: string
    }>,
  ) => {
    setHitlLabels(values.map((v) => v.value))
  }

  return (
    <div className={style.root} {...{ key: editedClient?._id }}>
      <div className={style.close} onClick={onClose}>
        <CloseIcon />
      </div>
      <form className="form" onSubmit={handleSubmit}>
        <div className={[style.row, style.grid].join(' ')}>
          <div className={style.companyColumn}>
            <TextInput
              value={company}
              theme={TextInputTheme.White}
              placeholder="Company name"
              onChange={(e: React.FormEvent<HTMLInputElement>) =>
                setCompany(e.currentTarget.value)
              }
              required
            />
          </div>
          <div className={style.limits}>
            {limitsOrder.map((key) => {
              const limit = limits[key]
              const value = limit === null ? '' : limit
              return <div key={key} className={style.limitItem}>
                <div className={style.limitLabel}>{initialLabels[key]}</div>
                <div className={style.limitInput}>
                  <TextInput
                    value={value}
                    theme={TextInputTheme.White}
                    onInput={(e: React.FormEvent<HTMLInputElement>) => {
                      const value: number | null = e.currentTarget.value === '' ? null : Number(e.currentTarget.value)
                      setLimits({ ...limits, [key]: value })
                    }}
                    type="number"
                    min={0}
                    placeholder="unlimited"
                  />
                  <div className={style.limitClear} onClick={() => {
                    setLimits({ ...limits, [key]: null })
                  }}>
                    <CloseIcon />
                  </div>
                </div>
              </div>
            })}
          </div>
          <div className={style.expiration}>
            <Datepicker
              datePickerProps={{
                minDate: new Date(),
              }}
              selectedDate={expire}
              onChange={handleDateChange}
              theme={TextInputTheme.White}
            />
          </div>
          <div className={style.owner}>
            {editedClient ? editedClient.owner : owner}
          </div>
        </div>
        <div className={style.row}>
          <TextInput
            value={website}
            theme={TextInputTheme.White}
            placeholder="Website"
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              setWebsite(e.currentTarget.value)
            }
          />
        </div>
        <div className={style.row}>
          <TextInput
            value={pipeline}
            theme={TextInputTheme.White}
            placeholder="Pipeline Link"
            onChange={(e: React.FormEvent<HTMLInputElement>) =>
              setPipeline(e.currentTarget.value)
            }
          />
        </div>
        <div className={style.labels}>
          <div className={style.heading}>Labels</div>
          <CreatableSelect
            isMulti
            value={labels.map((l) => ({ value: l, label: l }))}
            options={allLabels.map((l) => ({ value: l, label: l }))}
            onChange={onLabelsChange}
            closeMenuOnSelect={false}
          />
        </div>
        <div className={style.labels}>
          <div className={style.hitl}>
            <span>HITL</span>
            <Switch
              isChecked={isHitl}
              onChange={handleHitlChange}
              classname={style.switch}
            />
          </div>
          {isHitl && (
            //@ts-ignore
            <Select
              // defaultValue={labels.map((l) => ({ value: l, label: l }))}
              placeholder="Select document types for HITL"
              isMulti
              value={hitlLabels.map((l) => ({ value: l, label: l }))}
              options={allHitlLabels.map((l) => ({ value: l, label: l }))}
              onChange={onHitlLabelChange}
              closeMenuOnSelect={false}
            />
          )}
        </div>
        <div className={style.token}>
          <span className={style.tokenText}>Token</span>
          <span className={style.tokenValue}>{generatedToken}</span>
          <Button
            theme={ButtonTheme.BlueBordered}
            size={ButtonSize.XS}
            onClick={handleCopy}
          >
            copy
          </Button>
        </div>
        <div className={style.buttons}>
          <Button theme={ButtonTheme.Blue} isLoading={isLoading} type="submit">
            {editedClient ? 'Save' : 'Launch'}
          </Button>
          <Button
            theme={ButtonTheme.LinkButton}
            onClick={handleCancel}
            type="button"
          >
            Cancel
          </Button>
          {editedClient && (
            <div className={style.delete}>
              {editedClient.stopped ? (
                <Button
                  theme={ButtonTheme.Green}
                  // isLoading={isDeleteLoading}
                  onClick={handleActivate}
                  type="button"
                >
                  Resume
                </Button>
              ) : (
                <Button
                  theme={ButtonTheme.Orange}
                  // isLoading={isDeleteLoading}
                  onClick={handlePause}
                  type="button"
                >
                  Pause
                </Button>
              )}
              <Button
                theme={ButtonTheme.Red}
                isLoading={isDeleteLoading}
                onClick={handleDelete}
                className={style.deleteButton}
                type="button"
              >
                Delete
              </Button>
            </div>
          )}
        </div>
      </form>
    </div>
  )
}
